import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import {
  every,
  head,
  isEmpty,
  keys,
  lowerFirst,
  map,
  toLower,
  words
} from "lodash";
import moment from "moment";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useCommissionReports } from ".";
import AlertMessage from "../../components/AlertMessage";
import DateInput from "../../components/forms/DateInput";
import DropdownSearch from "../../components/forms/DropdownSearch";
import DropdownSelect, {
  DropdownSelectOption
} from "../../components/forms/DropdownSelect";
import FormProvider from "../../components/forms/FormProvider";
import Input from "../../components/forms/Input";
import { ApiResponse, fetchApi } from "../../utils/api";
import { locale } from "../../utils/dates";
import timezones from "../../utils/timezones";
import { Contact } from "../../utils/types";
import useCurrency, { CurrencyKey } from "../../utils/useCurrency";
import usePrevious from "../../utils/usePrevious";
import { useHeliumData } from "../heliumData";
import { getFormattedDate } from "../installs/InstallFormSidebar";
import { useWorkspaces } from "../workspaces";

interface Props {}

const GenerateCommissionReportSidebar: React.FC<Props> = () => {
  const dialogRef = useRef(null);
  const formRef = useRef<FormikProps<FormValues>>(null);
  const location = useLocation();
  const history = useHistory();
  const { blockchainHaltMoment } = useHeliumData();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { workspaces, currentWorkspaceId } = useWorkspaces();
  const {
    generateCommissionReport,
    generateCommissionReportSidebarConfig,
    setGenerateCommissionReportSidebarConfig
  } = useCommissionReports();
  const getCurrencyTitle = useCurrency((state) => state.getCurrencyTitle);
  const [currencyOptions, setCurrencyOptions] = useState<
    {
      key: string;
      title: string;
    }[]
  >([]);
  const [isValidMaxPeriodDays, setIsValidMaxPeriodDays] = useState(true);
  const [fetchStatus, setFetchStatus] = useState<
    "idle" | "fetching" | "fetched"
  >("idle");

  const { show, contacts } = useMemo(
    () => generateCommissionReportSidebarConfig,
    [generateCommissionReportSidebarConfig]
  );

  const initialTitle = useMemo(() => {
    let text = "Commission report";

    if (contacts.length === 1) {
      text += ` for ${(head(contacts) as Contact).name}`;
    }

    if (contacts.length > 1) {
      text += ` for ${contacts.length} contacts`;
    }

    return text;
  }, [contacts]);

  const fetchActiveCommissionCurrencies = useCallback(async () => {
    if (!currentWorkspaceId) return;

    setFetchStatus("fetching");

    try {
      const res = await fetchApi(
        `api/workspace/${currentWorkspaceId}/installs/commissions/currencies`
      );

      const apiResponse: ApiResponse<CurrencyKey[]> = await res.json();

      if (apiResponse.status && !isEmpty(apiResponse.data)) {
        const activeCommissionCurrencyOptions = map(
          apiResponse.data,
          (key) => ({ key, title: getCurrencyTitle(key) })
        );

        setCurrencyOptions([
          allCurrenciesOption,
          ...activeCommissionCurrencyOptions
        ]);
      }
    } catch (e) {
      console.error(e);
    }

    setFetchStatus("fetched");
  }, [currentWorkspaceId, getCurrencyTitle]);

  useEffect(() => {
    if (show && fetchStatus === "idle") {
      fetchActiveCommissionCurrencies();
    }
  }, [fetchActiveCommissionCurrencies, fetchStatus, show]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setFetchStatus("idle");
      setCurrencyOptions([]);
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  const getCurrencyOptionTitle = useCallback(
    (currencyOption: { key: string; title: string }) => {
      if (currencyOption.key === "all") {
        return "All";
      }
      return getCurrencyTitle(currencyOption.key as CurrencyKey);
    },
    [getCurrencyTitle]
  );

  const closeSidebar = useCallback(() => {
    setGenerateCommissionReportSidebarConfig({ show: false, contacts: [] });
    setIsValidMaxPeriodDays(true);
    setFetchStatus("idle");
    setCurrencyOptions([]);
  }, [setGenerateCommissionReportSidebarConfig]);

  const initialValues = useMemo(() => {
    const currentWorkspace = currentWorkspaceId
      ? workspaces[currentWorkspaceId]
      : undefined;

    return {
      title: initialTitle,
      start_date: moment()
        .local()
        .subtract(1, "month")
        .startOf("month")
        .toDate(),
      end_date: moment().local().startOf("month").toDate(),
      currency: allCurrenciesOption,
      timezone: currentWorkspace?.timezone
    };
  }, [currentWorkspaceId, initialTitle, workspaces]);

  const onSubmit = useCallback(
    async (
      { title, start_date, end_date, currency, timezone }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const requestValues = {
        title,
        start_date: getFormattedDate(start_date),
        end_date: getFormattedDate(end_date),
        currency: (currency as any).key === "all" ? null : currency.key,
        timezone
      };

      const apiResponse = await generateCommissionReport(
        requestValues,
        map(contacts, "uuid")
      );

      if (apiResponse?.status) {
        closeSidebar();

        const isOnCommissionsPage = matchPath(
          location.pathname,
          "/workspace/commission-reports"
        );

        if (!isOnCommissionsPage) {
          history.push("/workspace/commission-reports");
        }
      } else {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [
      closeSidebar,
      contacts,
      generateCommissionReport,
      history,
      location.pathname
    ]
  );

  const getTimezones = useCallback((searchInput: string | undefined) => {
    const filteredTimezones = keys(timezones).filter((timezone) => {
      if (searchInput) {
        const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

        return every(
          searchInputWords,
          (word) =>
            toLower(timezone).includes(word) ||
            toLower(timezones[timezone] || "").includes(word)
        );
      }
      return true;
    });

    return filteredTimezones;
  }, []);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={show}
        onClose={closeSidebar}
        initialFocus={dialogRef}
      >
        <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <div className="absolute inset-0" onClick={closeSidebar} />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-md h-full flex flex-col">
                <FormProvider
                  classnames={{ wrapper: "xl:max-w-7xl h-full" }}
                  onCancel={closeSidebar}
                  error={errorMessage}
                  disabled={
                    !isValidMaxPeriodDays ||
                    fetchStatus !== "fetched" ||
                    isEmpty(currencyOptions)
                  }
                  formikProps={{
                    onSubmit,
                    initialValues,
                    validationSchema: Yup.object().shape(
                      {
                        title: Yup.string().required("Required"),
                        start_date: Yup.date().when(
                          ["end_date"],
                          (
                            date: Date | undefined,
                            schema: Record<any, any>
                          ) => {
                            if (!date || !formRef.current?.values.start_date)
                              return schema;

                            setErrorMessage(null);
                            setIsValidMaxPeriodDays(true);

                            return schema.required("Required");
                          }
                        ),
                        end_date: Yup.date().required("Required"),
                        currency: Yup.object().required("Required"),
                        timezone: Yup.string().required("Required")
                      },
                      [["start_date", "end_Date"]]
                    ),
                    innerRef: formRef
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                  submitButtonText="Generate report"
                  submittingSubmitButtonText="Generating report"
                >
                  {({
                    isSubmitting,
                    values,
                    setFieldValue
                  }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              Generate {lowerFirst(initialTitle)}
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                autoFocus
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={closeSidebar}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              {fetchStatus === "fetched" &&
                              isEmpty(currencyOptions) ? (
                                <AlertMessage
                                  type="warning"
                                  title="No available commissions to report on"
                                  message="Commission reports are based on commission agreements defined in installs. Please create an install with a commission agreement to generate a commission report."
                                />
                              ) : blockchainHaltMoment &&
                                values.end_date &&
                                blockchainHaltMoment
                                  .local()
                                  .isBefore(moment(values.end_date)) ? (
                                <AlertMessage
                                  type="warning"
                                  title={`The Blockchain is currently outdated since ${blockchainHaltMoment.fromNow()}. It is advisable to generate your commission report later on.`}
                                />
                              ) : (
                                <AlertMessage
                                  type="warning"
                                  title="Disclaimer"
                                  message="Hotspotty will not be held liable for any indirect or accidental losses that will occur during the usage of the application. We do not claim that the entirety of the Hotspotty commissions module will be free of error although we will do our best efforts to maintain that the application will run as smoothly as possible. You will be responsible for all activity on your account."
                                  collapsible
                                />
                              )}

                              <Field
                                id="title"
                                name="title"
                                label="Title"
                                placeholder="Commission report"
                                type="text"
                                component={Input}
                                disabled={isSubmitting}
                              />

                              <Field
                                id="start_date"
                                name="start_date"
                                label="Start date"
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                max={moment().local().toDate()}
                                component={DateInput}
                                disabled={isSubmitting}
                              />

                              <Field
                                id="end_date"
                                name="end_date"
                                label="End date"
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                min={moment(values.start_date).local().toDate()}
                                component={DateInput}
                                disabled={
                                  isSubmitting ||
                                  moment(values.start_date)
                                    .local()
                                    .isAfter(moment())
                                }
                              />

                              <Field
                                className="-mt-1"
                                name="currency"
                                label="Currency"
                                disabled={
                                  isSubmitting ||
                                  fetchStatus !== "fetched" ||
                                  isEmpty(currencyOptions)
                                }
                                items={currencyOptions}
                                itemToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyOptionTitle(currencyOption)}
                                optionToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyOptionTitle(currencyOption)}
                                component={DropdownSelect}
                              />

                              <Field
                                name="timezone"
                                label="Timezone"
                                placeholder="Search a timezone"
                                disabled={isSubmitting}
                                fetchItems={getTimezones}
                                itemToString={(item: string) => timezones[item]}
                                component={DropdownSearch}
                                withoutMinimumSearchCharacters
                                debounceTime={0}
                                initialItems={getTimezones("")}
                                clearInputOnSelect={false}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default GenerateCommissionReportSidebar;

//
// Utils
//

interface FormValues {
  title: string;
  start_date: string;
  end_date: string;
  currency: DropdownSelectOption<CurrencyKey>;
  timezone: string;
}

const ORDERED_FIELD_NAMES = [
  "title",
  "startDate",
  "endDate",
  "currency",
  "timezone"
];

export interface GenerateCommissionReportSidebarConfig {
  show: boolean;
  contacts: Contact[];
}

//
// Utils
//

const allCurrenciesOption = { key: "all", title: "All" };
